import PropTypes from "prop-types";
import { AgGridReact } from "ag-grid-react";
import { useMemo, useRef, useCallback } from "react";
import { Typography, Box, Accordion, AccordionSummary, Stack, IconButton, Button } from "@mui/material";
import { ArrowDownward, Download, RestoreFromTrash } from "@mui/icons-material";
import Visibility from "@mui/icons-material/Visibility";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { enqueueSnackbar } from "notistack";
import { objectArrayMin } from "../../utils/math";
import AGGridQuickFilterWrapper from "../../reuseable-components/AGGrid/AGGridQuickFilterWrapper";
import AgGridDefaults from '../../reuseable-components/AGGrid/AGGridDefaults';
import axiosInstance from "../../utils/axios";
import { fileDownload } from "../../utils/fileDownload";
import DropZone from "../../reuseable-components/Dropzone/DropZone";

InvoiceFiles.propTypes = {
  files: PropTypes.array,
  setFiles: PropTypes.func,
  setActiveFile: PropTypes.func,
  activeFile: PropTypes.object,
};

export default function InvoiceFiles({ files, setFiles, activeFile, setActiveFile }) {
  const gridRef = useRef(null);

  const onDrop = useCallback(
    (acceptedFiles) => {
      setFiles((files) => [
        ...files,
        ...acceptedFiles.map((file) => ({
          id: objectArrayMin(files, "id") - 1,
          file,
          fileName: file.name,
          localFile: true,
        })),
      ]);
    },
    [setFiles]
  );

  const columns = useMemo(
    () => [
      {
        field: "fileName",
        minWidth: 150,
        flex: 1,
      },
      {
        field: "actions",
        minWidth: 100,
        cellRenderer: (params) => (
          <Stack direction="row" spacing={1}>
            {!params.data.delete && params.data.fileName.toLowerCase().endsWith(".pdf") && (
              <IconButton
                disableRipple
                sx={{ px: 0 }}
                color="info"
                onClick={() => setActiveFile(params.data.id === activeFile?.id ? null : params.data)}
              >
                {params.data.id === activeFile?.id ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            )}
            {!params.data.delete && !params.data.localFile && (
              <IconButton
                disableRipple
                sx={{ px: 0 }}
                color="info"
                onClick={() => {
                  axiosInstance
                    .get(`/invoice/downloadInvoiceAttachment?id=${params.data.id}`, { responseType: "blob" })
                    .then((res) => fileDownload(res.data, params.data.fileName))
                    .catch(() => enqueueSnackbar("Invoice file fetch failed", { variant: "error" }));
                }}
              >
                <Download />
              </IconButton>
            )}
            <IconButton
              disableRipple
              sx={{ px: 0 }}
              color="error"
              onClick={() => {
                setActiveFile(params.data.id === activeFile?.id ? null : activeFile);
                setFiles((files) => {
                  const oldFile = files.find((file) => file.id === params.data.id);
                  if (!oldFile) return files;
                  if (oldFile.localFile) return files.filter((file) => file.id !== params.data.id);
                  oldFile.delete = !(oldFile.delete ?? false);
                  params.node.setData(oldFile);
                  return [...files.filter((file) => file.id !== params.data.id), oldFile];
                });
              }}
            >
              {params.data.delete ? <RestoreFromTrash /> : <DeleteOutline />}
            </IconButton>
          </Stack>
        ),
      },
    ],
    [activeFile, setFiles, setActiveFile]
  );

  return (
    <Box sx={{ border: "solid thin rgb(43 45 47 / 24%)" }}>
      <Accordion>
        <AccordionSummary expandIcon={<ArrowDownward />}>
          <Typography component="span">Files</Typography>
        </AccordionSummary>
        <Stack sx={{ mx: 2, mb: 2 }} spacing={1}>
          <AGGridQuickFilterWrapper gridRef={gridRef}>
            <Button variant="contained" component="label">
              Upload File
              <input
                type="file"
                hidden
                onChange={(e) => {
                  if (e.target.files && e.target.files.length) {
                    setFiles((files) => [
                      ...files,
                      { id: objectArrayMin(files, "id") - 1, file: e.target.files[0], fileName: e.target.files[0].name, localFile: true },
                    ]);
                  }
                }}
              />
            </Button>
          </AGGridQuickFilterWrapper>

          <DropZone onDrop={onDrop}>
            <Box sx={{ height: "250px" }}>
              <AgGridReact ref={gridRef} {...AgGridDefaults} columnDefs={columns} rowData={files} />
            </Box>
          </DropZone>
        </Stack>
      </Accordion>
    </Box>
  );
}
